<template>
  <div class="container mt-4">
    <div class="row" v-if="!user">
      <div class="col-12 w-100 text-center">
        <HMSpinner />
      </div>
    </div>
    <div v-else>
      <div class="row justify-content-center mt-4">
        <CommunityProfileMainData
          :user="user"
          :primaryColor="primaryColor"
        ></CommunityProfileMainData>
      </div>
      <hr class="my-4" />
      <div
        class="d-flex flex-column align-items-center justify-content-between mt-4"
        v-if="user"
      >
        <div
          class="d-flex align-items-center justify-content-end flex-grow-1"
          :class="{ 'justify-content-center': !canShowMessageButton }"
        >
          <div class="pr-3" v-if="user.location">
            <i class="icon fas fa-lg fa-map-marker-alt pr-1"></i>
            {{ user.location }}
          </div>
          <div class="pr-3">
            <i class="icon far fa-lg fa-comment pr-1"></i>
            {{ $t('STUDIO.LABELS.COMMENTS') }}: {{ user.comments_count }}
          </div>
          <div class="pr-3">
            <i class="icon far fa-lg fa-comments pr-1"></i>
            {{ $t('STUDIO.LABELS.POSTS') }}: {{ user.posts_count }}
          </div>
        </div>
        <div v-if="canShowMessageButton" class="mt-3">
          <HMButton
            :disabled="!sendMessageButtonActive"
            color="outline-gray"
            class="mr-3"
            @click="createPrivateChatRoom"
          >
            <i class="fal fa-comments c-brand-color pr-2"></i>
            {{ $t('COMMUNITY.BUTTON.SEND_MESSAGE') }}
          </HMButton>
        </div>
      </div>
      <hr class="my-4" />
      <div class="d-flex justify-content-center">
        <p>
          {{ user.description_html }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import CommunityProfileMainData from '@/web/components/community/CommunityProfileMainData';
import { mapGetters } from 'vuex';
import { comments } from '@/web/components/community/community-comments.mock.js';
import { errorToMessage } from '@/core/utils/errors';
import { getStudioURL } from '@/web/router/custom-domain-utils';
import HMSpinner from '@/web/components/common/HMSpinner';

export default Vue.extend({
  name: 'StudentCommunityProfileView',
  components: {
    HMSpinner,
    CommunityProfileMainData,
  },
  data() {
    return {
      user: null,
      comments: comments,
      sendMessageButtonActive: true,
    };
  },
  computed: {
    ...mapGetters({
      studio: 'studio/currentStudio',
      currentUserId: 'user/studentCommunityProfileId',
    }),
    studioURL() {
      return getStudioURL(this.$route);
    },
    canShowMessageButton() {
      if (!this.user) return false;
      return this.user?.id !== this.currentUserId;
    },
    primaryColor() {
      return this.studio?.primary_color;
    },
  },
  methods: {
    async loadUserInfo(id) {
      if (!id) return;
      try {
        this.user = await this.$store.dispatch('community/loadUserData', {
          studioURL: this.studioURL,
          uuid: id,
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Could not load this profile',
          text: errorToMessage(error.response),
        });
      }
    },
    async redirectToChat(roomId) {
      this.$router.push({
        name: 'studio-community-chat',
        params: {
          studio_url: getStudioURL(this.$route),
        },
        query: {
          room_id: roomId,
        },
      });
    },
    async createPrivateChatRoom() {
      try {
        this.sendMessageButtonActive = false;
        const privateRoom = await this.$store.dispatch(
          'community/createPrivateChatRoom',
          {
            studioURL: this.studioURL,
            member: this.user.id,
          }
        );
        this.sendMessageButtonActive = true;
        this.redirectToChat(privateRoom.id);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1,
        });
      }
    },
  },
  watch: {
    '$route.params.id': {
      handler: function (id) {
        this.loadUserInfo(id);
      },
      immediate: true,
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.container {
  max-width: 616px;
}

.icon {
  color: var(--brand-color, $coral);
}
</style>
